import React from 'react';

/**
 * Custom Message Box Component
 * Replaces native alert() with a styled modal
 * 
 * Usage:
 * const [message, setMessage] = useState({ show: false, text: '', type: 'info' });
 * const showMessage = (text, type = 'info') => setMessage({ show: true, text, type });
 * const closeMessage = () => setMessage({ show: false, text: '', type: 'info' });
 * 
 * <MessageBox message={message} onClose={closeMessage} />
 */
export default function MessageBox({ message, onClose }) {
  if (!message.show) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 11000,
      backdropFilter: 'blur(2px)'
    }}>
      <div style={{
        background: 'white',
        padding: '30px',
        borderRadius: '12px',
        maxWidth: '500px',
        width: '90%',
        boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)',
        border: `3px solid ${message.type === 'error' ? '#f44336' : message.type === 'success' ? '#4CAF50' : '#2196F3'}`,
        animation: 'messageBoxFadeIn 0.2s ease-out'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '15px',
          marginBottom: '20px'
        }}>
          <div style={{
            width: '50px',
            height: '50px',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '24px',
            background: message.type === 'error' ? '#FFEBEE' : message.type === 'success' ? '#E8F5E9' : '#E3F2FD',
            color: message.type === 'error' ? '#f44336' : message.type === 'success' ? '#4CAF50' : '#2196F3'
          }}>
            {message.type === 'error' ? '✕' : message.type === 'success' ? '✓' : 'ℹ'}
          </div>
          <h3 style={{
            margin: 0,
            fontSize: '18px',
            fontWeight: 'bold',
            color: message.type === 'error' ? '#f44336' : message.type === 'success' ? '#4CAF50' : '#2196F3'
          }}>
            {message.type === 'error' ? 'Error' : message.type === 'success' ? 'Success' : 'Information'}
          </h3>
        </div>
        <p style={{
          margin: '0 0 25px 0',
          fontSize: '16px',
          lineHeight: '1.5',
          color: '#333',
          whiteSpace: 'pre-wrap'
        }}>
          {message.text}
        </p>
        <button
          onClick={onClose}
          style={{
            width: '100%',
            padding: '12px 24px',
            background: message.type === 'error' ? '#f44336' : message.type === 'success' ? '#4CAF50' : '#2196F3',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            fontSize: '16px',
            fontWeight: 'bold',
            cursor: 'pointer',
            transition: 'all 0.2s'
          }}
          onMouseOver={(e) => e.target.style.opacity = '0.9'}
          onMouseOut={(e) => e.target.style.opacity = '1'}
        >
          OK
        </button>
      </div>
      <style>{`
        @keyframes messageBoxFadeIn {
          from {
            opacity: 0;
            transform: scale(0.9);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
      `}</style>
    </div>
  );
}
